import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, KeyRound, Fingerprint, Timer, Smartphone, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UNIVERSAL_MODELS } from '../lib/store';
import ByokModels from './ByokModels';
import KeyStatusBanner from './KeyStatusBanner';
import { reportKeyStatus } from '../lib/keyStatus';
import { BYOK_MODELS, BYOK_PROVIDERS, modelCache, sanitizeKey, maskKey, detectProvider, providerOf, testBYOKConnection } from '../lib/models';
import { pingArchitect, aiPayload } from '../lib/architect';
import { guard, biometricAvailable, registerBiometric, removeBiometric, listPasskeys, localPasskey } from '../lib/crypto';
import { devApi, devToken } from '../lib/api';

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="glass glass-edge rounded-2xl p-5 sm:p-6 rise">
      <div className="flex items-center gap-2 mb-5"><Icon className="h-4 w-4 text-cyan-300" /><h2 className="text-sm font-bold tracking-wider text-slate-100">{title}</h2></div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Pick({ value, onChange, items, testid }) {
  return (
    <Select value={String(value)} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-black/30"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-titan-surface border-violet-500/30">{items.map((i) => <SelectItem key={i.id} value={String(i.id)}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ModelInfo({ id }) {
  const m = BYOK_MODELS.find((x) => x.id === id);
  if (!m) return null;
  const tone = m.badge === 'Fast' ? 'bg-amber-500/15 text-amber-300' : m.badge === 'Economical' ? 'bg-white/10 text-slate-300' : 'bg-emerald-500/15 text-emerald-300';
  return (
    <div data-testid="byok-model-info" className="flex items-start gap-2 text-[10px] text-slate-400 t-chat -mt-1">
      <span className={`shrink-0 rounded-full px-2 py-0.5 font-bold ${tone}`}>{m.badge}</span>
      <span>{m.description} · ID <code className="text-cyan-300">{m.id}</code> · output max {m.maxOutputTokens.toLocaleString('it-IT')} token</span>
    </div>
  );
}

function AiCard({ settings, updateSettings }) {
  const [show, setShow] = useState(false);
  const [ping, setPing] = useState(null);
  const [busy, setBusy] = useState(false);
  const byok = settings.aiMode === 'byok';
  const test = async () => {
    setBusy(true); setPing(null);
    try {
      if (byok) {
        if (!settings.apiKey) throw new Error('Inserisci prima la tua chiave API');
        if (!settings.byokModel?.trim()) throw new Error('Scegli un modello o inserisci un ID personalizzato');
        if (detectProvider(settings.apiKey) && detectProvider(settings.apiKey) !== provider) throw new Error(`La chiave non è di ${provider === 'anthropic' ? 'Anthropic' : 'OpenAI'}: scegli il provider giusto`);
        const r = await testBYOKConnection(provider, settings.apiKey, settings.byokModel);
        // OpenAI omits CORS headers on error responses: let the server read the real HTTP status
        const res = r.blocked ? await pingArchitect(aiPayload(settings)) : r;
        setPing(res);
        if (res.key_status) reportKeyStatus(res.key_status, { silent: false });
      } else setPing(await pingArchitect(aiPayload(settings)));
    } catch (e) { setPing({ ok: false, error: e.message }); }
    setBusy(false);
  };
  const onKey = (apiKey) => {
    const p = detectProvider(apiKey);
    const patch = { apiKey };
    if (p && provider !== p) { modelCache.clear(); patch.byokProvider = p; patch.byokModel = BYOK_PROVIDERS[p].defaultModelId; toast(`Chiave ${p === 'anthropic' ? 'Anthropic' : 'OpenAI'} riconosciuta`); }
    updateSettings(patch); setPing(null);
  };
  const provider = settings.byokProvider || providerOf(settings.byokModel);
  return (
    <Card icon={Radio} title="Gestione Intelligente API" testid="config-ai">
      <div className="grid grid-cols-2 gap-2">
        {[['universal', 'Crediti Emergent', 'Disattivati: non usati'], ['byok', 'Chiave personale', 'BYOK OpenAI / Claude · attiva']].map(([id, t, d]) => (
          <button key={id} data-testid={`ai-mode-${id}`} disabled={id === 'universal'} onClick={() => updateSettings({ aiMode: id })}
            className={`rounded-xl border p-3 text-left transition-colors ${settings.aiMode === id ? 'border-violet-400 bg-violet-500/15' : 'border-white/10 hover:bg-white/5'} disabled:opacity-40 disabled:cursor-not-allowed`}>
            <div className="text-xs font-bold text-slate-100">{t}</div><div className="text-[10px] text-slate-400 mt-0.5">{d}</div>
          </button>
        ))}
      </div>
      {byok ? (
        <>
          <ByokModels key={provider} settings={{ ...settings, byokProvider: provider }} updateSettings={updateSettings} onChange={() => setPing(null)} />
          <ModelInfo id={settings.byokModel} />
          <div data-testid="byok-provider" className="text-[10px] text-slate-400">Provider: <b className="text-cyan-300">{provider === 'anthropic' ? 'Anthropic (sk-ant-…)' : 'OpenAI (sk-…)'}</b> · riconosciuto in automatico dalla chiave · al cambio di chiave o provider l'elenco va aggiornato di nuovo</div>
          <div className="relative">
            <Input data-testid="byok-key-input" type={show ? 'text' : 'password'} value={settings.apiKey} onChange={(e) => onKey(sanitizeKey(e.target.value))}
              placeholder={provider === 'openai' ? 'sk-…' : 'sk-ant-…'} className="bg-black/30 pr-10 font-mono text-xs" />
            <button data-testid="byok-key-toggle" aria-label="Mostra o nascondi chiave" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
          </div>
          <p className="text-[10px] text-slate-500 t-chat">La chiave è salvata solo su questo dispositivo, cifrata (AES-GCM) con una chiave del browser non esportabile: protegge da copie dei file, ma non da codice che gira dentro l'app stessa. Non finisce mai nella cache dei modelli. Il test e l'elenco modelli partono direttamente da qui verso {provider === 'anthropic' ? 'api.anthropic.com' : 'api.openai.com'}; se il browser li blocca, e per creare i giochi, passa dal tuo server solo per la singola richiesta, senza essere salvata né scritta nei log.</p>
        </>
      ) : (
        <Pick testid="universal-model-select" value={settings.universalModel} onChange={(v) => updateSettings({ universalModel: v })} items={UNIVERSAL_MODELS.map((m) => ({ ...m, label: `${m.label} — ${m.note}` }))} />
      )}
      <button data-testid="ai-ping-btn" onClick={test} disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />} TEST CONNESSIONE (PING)
      </button>
      {ping && (
        <div data-testid="ai-ping-result" className={`rounded-lg border p-3 text-xs ${ping.ok ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5'}`}>
          {ping.ok ? (
            <>
              <div data-testid="ai-ping-online" className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/50 px-3 py-1 text-emerald-300 font-extrabold tracking-widest animate-pulse"><span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" />TITAN ONLINE — {ping.latency_ms}ms</div>
              {ping.reply && <div className="text-slate-400 mt-1">Risposta: "{ping.reply}"</div>}
              {byok && <div data-testid="ai-ping-byok-info" className="text-slate-400 mt-1">Chiave {maskKey(settings.apiKey)} · modello <code className="text-cyan-300">{ping.model_used || ping.model}</code>{ping.requested_model && ping.requested_model !== ping.model_used ? ` (migrato da ${ping.requested_model})` : ''} · ping da 1 token</div>}
              {ping.usage && <div className="text-slate-400 mt-1">Consumi stimati: {ping.usage.requests} richieste · {(ping.usage.tokens_in + ping.usage.tokens_out).toLocaleString('it-IT')} token · ~${ping.usage.est_cost_usd}</div>}
            </>
          ) : <div data-testid="ai-ping-error" className="text-rose-300">✗ {ping.error}{ping.model_used && <div className="text-slate-400 mt-1">Modello inviato: <code className="text-cyan-300">{ping.model_used}</code>{ping.requested_model !== ping.model_used ? ` (migrato da ${ping.requested_model})` : ''}</div>}</div>}
        </div>
      )}
      <KeyStatusBanner provider={provider} />
      <CoverKey settings={settings} updateSettings={updateSettings} />
    </Card>
  );
}

function CoverKey({ settings, updateSettings }) {
  const [show, setShow] = useState(false);
  const [check, setCheck] = useState(null);
  const verify = async () => {
    setCheck({ busy: true });
    const r = await testBYOKConnection('openai', settings.coverKey, 'gpt-4o-mini');
    const res = r.blocked ? await pingArchitect({ ai_mode: 'byok', model: 'gpt-4o-mini', provider: 'openai', api_key: settings.coverKey }) : r;
    if (res.key_status) reportKeyStatus(res.key_status);
    setCheck(res);
  };
  const mainIsOpenAI = (settings.byokProvider || providerOf(settings.byokModel)) === 'openai' && !!settings.apiKey;
  return (
    <div data-testid="cover-key" className="space-y-1.5 border-t border-white/10 pt-3">
      <div className="text-xs font-bold text-slate-100">Chiave OpenAI · copertine IA e voce</div>
      {!settings.coverKey && !mainIsOpenAI && (
        <ol data-testid="openai-key-guide" className="list-decimal pl-4 text-[10px] text-slate-400 t-chat space-y-0.5">
          <li>Apri <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-cyan-300 underline">platform.openai.com/api-keys</a> e accedi (o crea l'account).</li>
          <li>In <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noreferrer" className="text-cyan-300 underline">Billing</a> aggiungi un credito (bastano pochi euro).</li>
          <li>Premi “Create new secret key”, copiala e incollala qui sotto, poi “Verifica”.</li>
        </ol>
      )}
      {mainIsOpenAI && !settings.coverKey
        ? <div data-testid="cover-key-main" className="text-[10px] text-emerald-300 t-chat">Uso la tua chiave principale OpenAI. Puoi indicarne un'altra qui sotto.</div>
        : <div className="text-[10px] text-slate-400 t-chat">Le copertine usano una chiave OpenAI (costo sul tuo account OpenAI). Salvata cifrata solo su questo dispositivo, come la chiave principale.</div>}
      <div className="relative">
        <Input data-testid="cover-key-input" type={show ? 'text' : 'password'} value={settings.coverKey || ''} placeholder="sk-… (OpenAI)" autoComplete="off" spellCheck={false}
          onChange={(e) => updateSettings({ coverKey: sanitizeKey(e.target.value) })} className="bg-black/30 pr-10 font-mono text-xs" />
        <button data-testid="cover-key-toggle" onClick={() => setShow(!show)} aria-label={show ? 'Nascondi chiave' : 'Mostra chiave'} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      </div>
      {settings.coverKey && detectProvider(settings.coverKey) !== 'anthropic' && (
        <button data-testid="cover-key-verify" onClick={verify} disabled={check?.busy} className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-[11px] font-bold text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50">{check?.busy ? 'Verifica…' : 'Verifica chiave OpenAI'}</button>
      )}
      {check && !check.busy && <div data-testid="cover-key-result" className={`text-[10px] ${check.ok ? 'text-emerald-300' : 'text-rose-300'}`}>{check.ok ? `✓ Chiave attiva: copertine e voce pronte (${check.latency_ms}ms)` : `✗ ${check.error}`}</div>}
      {settings.coverKey && detectProvider(settings.coverKey) === 'anthropic' && <div data-testid="cover-key-wrong" className="text-[10px] text-rose-300">Questa è una chiave Anthropic: per le copertine serve una chiave OpenAI.</div>}
    </div>
  );
}

function SecurityCard({ settings, updateSettings }) {
  const [avail, setAvail] = useState(false);
  const [keys, setKeys] = useState([]);
  const reload = () => listPasskeys().then(setKeys).catch(() => setKeys([]));
  const mine = keys.find((k) => k.credential_id === localPasskey.get());
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  useEffect(() => { biometricAvailable().then(setAvail); reload(); }, []); // eslint-disable-line
  const enroll = async () => {
    try { await registerBiometric(); reload(); toast.success('Face ID / Impronta attivati: ora puoi entrare senza PIN'); } catch (e) { toast.error(e.status ? e.message : 'Registrazione biometrica annullata o non supportata'); }
  };
  const changePin = async () => {
    if (!/^\d{6}$/.test(next)) return toast.error('Il nuovo PIN deve avere 6 cifre');
    if ((await guard.verify(cur)) !== true && !devToken.get()) return toast.error('PIN attuale errato');
    try { await devApi.changePin(cur, next); } catch (e) { return toast.error(e.message); }
    await guard.sync(next);
    setCur(''); setNext(''); toast.success('PIN aggiornato anche sul server');
  };
  return (
    <Card icon={ShieldCheck} title="Titan Guard" testid="config-security">
      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 p-3">
        <div className="flex items-center gap-3"><Fingerprint className="h-5 w-5 text-violet-300" /><div><div className="text-xs text-slate-100">Face ID / Impronta (WebAuthn)</div><div className="text-[10px] text-slate-500">{avail ? (mine ? 'Attivo su questo dispositivo' : 'Disponibile') : 'Non disponibile su questo dispositivo/browser'}</div></div></div>
        {mine
          ? <button data-testid="bio-remove-btn" onClick={async () => { await removeBiometric(mine.credential_id).catch((e) => toast.error(e.message)); reload(); }} className="rounded-lg border border-rose-500/40 px-3 py-1.5 text-[10px] text-rose-300">Rimuovi</button>
          : <button data-testid="bio-enroll-btn" disabled={!avail} onClick={enroll} className="rounded-lg bg-violet-600 px-3 py-1.5 text-[10px] font-bold disabled:opacity-30">Attiva</button>}
      </div>
      {keys.filter((k) => k !== mine).length > 0 && (
        <div data-testid="bio-devices" className="space-y-1">
          <div className="text-[10px] text-slate-500">Altri dispositivi autorizzati</div>
          {keys.filter((k) => k !== mine).map((k) => (
            <div key={k.credential_id} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-1.5 text-[11px] text-slate-300">
              <span>{k.label} · {k.created_at?.slice(0, 10)}</span>
              <button data-testid={`bio-revoke-${k.credential_id.slice(0, 8)}`} onClick={async () => { await removeBiometric(k.credential_id).catch((e) => toast.error(e.message)); reload(); }} className="text-rose-300 text-[10px]">Revoca</button>
            </div>
          ))}
        </div>
      )}
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-300 mb-2"><Timer className="h-4 w-4" />Auto-lock per inattività</div>
        <Pick testid="autolock-select" value={settings.autoLockMin} onChange={(v) => updateSettings({ autoLockMin: Number(v), autoLockChosen: true })}
          items={[{ id: 1, label: '1 minuto' }, { id: 5, label: '5 minuti' }, { id: 15, label: '15 minuti' }, { id: 30, label: '30 minuti' }, { id: 0, label: 'Mai' }]} />
      </div>
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-300 mb-2"><KeyRound className="h-4 w-4" />Cambia PIN</div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input data-testid="pin-current-input" type="password" inputMode="numeric" maxLength={6} value={cur} onChange={(e) => setCur(e.target.value.replace(/\D/g, ''))} placeholder="Attuale" className="bg-black/30" />
          <Input data-testid="pin-new-input" type="password" inputMode="numeric" maxLength={6} value={next} onChange={(e) => setNext(e.target.value.replace(/\D/g, ''))} placeholder="Nuovo" className="bg-black/30" />
          <button data-testid="pin-change-btn" onClick={changePin} className="rounded-lg bg-violet-600 px-3 text-xs font-bold">OK</button>
        </div>
      </div>
    </Card>
  );
}

function RuntimeCard({ settings, updateSettings }) {
  return (
    <Card icon={Smartphone} title="Hardware & Runtime" testid="config-runtime">
      <div>
        <div className="text-xs text-slate-300 mb-2">Blocco orientamento (a schermo intero)</div>
        <Pick testid="orientation-select" value={settings.orientation} onChange={(v) => updateSettings({ orientation: v })}
          items={[{ id: 'auto', label: 'Sensore libero' }, { id: 'landscape', label: 'Sensor-landscape' }, { id: 'portrait', label: 'Portrait' }]} />
      </div>
      <div>
        <div className="text-xs text-slate-300 mb-2">Frame limiter</div>
        <Pick testid="fps-select" value={settings.fps} onChange={(v) => updateSettings({ fps: Number(v) })} items={[30, 60, 120].map((f) => ({ id: f, label: `${f} FPS` }))} />
      </div>
      <p className="text-[10px] text-slate-500 t-chat">Controller DualSense, Xbox Wireless e 8BitDo vengono riconosciuti automaticamente via Gamepad API (Bluetooth o USB) appena premi un tasto.</p>
    </Card>
  );
}

export default function ConfigPanel(props) {
  return (
    <div data-testid="config-view" className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="t-label mb-3">Pannello di configurazione</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-50 mb-6">Config<span className="text-cyan-400">.</span></h1>
        <Link to="/legal" data-testid="config-privacy-link" className="mb-8 inline-flex items-center gap-2 rounded-full border border-cyan-500/40 px-4 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/10 transition-colors"><ShieldCheck className="h-4 w-4" />Privacy, Termini, Cookie e Note Legali</Link>
        <div className="grid gap-6 lg:grid-cols-2">
          <AiCard {...props} />
          <div className="space-y-6"><SecurityCard {...props} /><RuntimeCard {...props} /></div>
        </div>
      </div>
    </div>
  );
}
